import { Trash2 } from 'lucide-react';
import { formatPrice, useTranslation } from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { PreparedOrderLine } from '../lib/admin-prepared-order-schema';
import { calculatePreparedOrderSubtotal } from '../lib/admin-prepared-order-schema';

export interface AdminPreparedOrderLinesProps {
  readonly lines: readonly PreparedOrderLine[];
  readonly onQuantityChange: (variantId: string, quantity: number) => void;
  readonly onRemove: (variantId: string) => void;
}

function lineQuantityErrorKey(line: PreparedOrderLine): TranslationKey | null {
  if (!Number.isInteger(line.quantity) || line.quantity <= 0) {
    return 'admin.orders.prepare.lines.quantityWhole' as TranslationKey;
  }
  if (line.quantity > line.variant.available_stock) {
    return 'admin.orders.prepare.lines.quantityStock' as TranslationKey;
  }
  return null;
}

export function AdminPreparedOrderLines(props: AdminPreparedOrderLinesProps): React.JSX.Element {
  const { t, lang } = useTranslation();
  const subtotal = calculatePreparedOrderSubtotal(props.lines);

  return (
    <section className="border-border bg-card shadow-admin-card rounded-lg border p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-display text-foreground text-xl font-semibold">
          {t('admin.orders.prepare.lines.title' as TranslationKey)}
        </h2>
        <span className="text-foreground text-sm font-semibold">
          {t('admin.orders.prepare.lines.subtotal' as TranslationKey)} {formatPrice(subtotal, lang)}
        </span>
      </div>

      {props.lines.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t('admin.orders.prepare.lines.empty' as TranslationKey)}
        </p>
      ) : (
        <div className="space-y-3">
          {props.lines.map((line) => {
            const errorKey = lineQuantityErrorKey(line);
            const quantityId = `prepared-order-quantity-${line.variant.variant_id}`;
            const errorId = `${quantityId}-error`;
            const validQuantity = Number.isInteger(line.quantity) && line.quantity > 0;
            const lineTotalCents = validQuantity
              ? line.quantity * line.variant.effective_price_cents
              : 0;
            return (
              <article
                key={line.variant.variant_id}
                className="border-border bg-background grid gap-3 rounded-lg border p-3"
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0 text-start">
                    <p className="text-foreground break-words text-sm font-medium leading-snug">
                      {line.variant.product_name}
                    </p>
                    <p className="text-muted-foreground mt-1 break-words text-xs">
                      {line.variant.option_summary}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('admin.orders.prepare.lines.remove' as TranslationKey)}
                    onClick={() => props.onRemove(line.variant.variant_id)}
                    className="shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-[minmax(7rem,9rem)_minmax(0,1fr)] sm:items-start">
                  <label className="text-muted-foreground text-xs" htmlFor={quantityId}>
                    {t('admin.orders.prepare.lines.quantity' as TranslationKey)}
                    <Input
                      id={quantityId}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={line.variant.available_stock}
                      step={1}
                      value={Number.isFinite(line.quantity) ? String(line.quantity) : ''}
                      aria-invalid={errorKey ? true : undefined}
                      aria-describedby={errorKey ? errorId : undefined}
                      onChange={(event) => {
                        const rawValue = event.target.value;
                        props.onQuantityChange(
                          line.variant.variant_id,
                          rawValue === '' ? Number.NaN : Number(rawValue),
                        );
                      }}
                      className="mt-1"
                    />
                    {errorKey ? (
                      <span
                        id={errorId}
                        className="text-destructive mt-1 block text-xs"
                        role="alert"
                      >
                        {t(errorKey, { count: line.variant.available_stock })}
                      </span>
                    ) : null}
                  </label>
                  <div className="text-start text-sm">
                    <p className="text-muted-foreground text-xs">
                      {t('admin.orders.prepare.lines.lineTotal' as TranslationKey)}
                    </p>
                    <p className="text-foreground break-words font-medium">
                      {formatPrice(lineTotalCents, lang)}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
